import React, { useState, useEffect, useRef } from 'react';

interface BlogSearchProps {
    value: string;
    onSearch: (query: string) => void;
    placeholder?: string;
}

interface SearchSuggestion {
    type: 'recent' | 'popular' | 'suggestion';
    text: string;
    icon: string;
}

export default function BlogSearch({ value, onSearch, placeholder = "Search..." }: BlogSearchProps) {
    const [localValue, setLocalValue] = useState(value);
    const [isOpen, setIsOpen] = useState(false);
    const [suggestions] = useState<SearchSuggestion[]>([
        { type: 'popular', text: 'English grammar rules', icon: '📚' },
        { type: 'popular', text: 'Vocabulary building tips', icon: '🔤' },
        { type: 'popular', text: 'AI learning techniques', icon: '🤖' },
        { type: 'popular', text: 'Success stories', icon: '🏆' },
        { type: 'suggestion', text: 'How to improve pronunciation', icon: '🗣️' },
        { type: 'suggestion', text: 'Common English mistakes', icon: '❌' },
        { type: 'suggestion', text: 'Business English phrases', icon: '💼' },
        { type: 'suggestion', text: 'Learning motivation tips', icon: '💪' }
    ]);

    const searchRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const debounceRef = useRef<NodeJS.Timeout | null>(null);

    // Sync with external value changes
    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    // Debounced search
    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            if (localValue !== value) {
                onSearch(localValue);
            }
        }, 300);

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [localValue, onSearch, value]);

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalValue(e.target.value);
        setIsOpen(true);
    };

    const handleSuggestionClick = (suggestion: string) => {
        setLocalValue(suggestion);
        onSearch(suggestion);
        setIsOpen(false);
        inputRef.current?.blur();
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSearch(localValue);
        setIsOpen(false);
        inputRef.current?.blur();
    };

    const handleClear = () => {
        setLocalValue('');
        onSearch('');
        inputRef.current?.focus();
    };

    const filteredSuggestions = suggestions.filter(suggestion =>
        localValue.length === 0 ||
        suggestion.text.toLowerCase().includes(localValue.toLowerCase())
    );

    const popularSuggestions = filteredSuggestions.filter(s => s.type === 'popular');
    const otherSuggestions = filteredSuggestions.filter(s => s.type !== 'popular');

    return (
        <div ref={searchRef} className="relative">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">🔍 Search</h3>

                <form onSubmit={handleSubmit} className="relative">
                    <div className="relative">
                        <input
                            ref={inputRef}
                            type="text"
                            value={localValue}
                            onChange={handleInputChange}
                            onFocus={() => setIsOpen(true)}
                            placeholder={placeholder}
                            className="w-full pl-12 pr-10 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                        />

                        {/* Search Icon */}
                        <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>

                        {/* Clear Button */}
                        {localValue && (
                            <button
                                type="button"
                                onClick={handleClear}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>

                    {/* Search Suggestions Dropdown */}
                    {isOpen && (
                        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
                            {/* Current search */}
                            {localValue && (
                                <div className="border-b border-gray-100">
                                    <button
                                        type="submit"
                                        className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center space-x-3"
                                    >
                                        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                        <span className="text-gray-700">Search for "<span className="font-semibold text-blue-600">{localValue}</span>"</span>
                                    </button>
                                </div>
                            )}

                            {/* Popular searches */}
                            {popularSuggestions.length > 0 && (
                                <div className="border-b border-gray-100">
                                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50">
                                        Popular Searches
                                    </div>
                                    {popularSuggestions.map((suggestion, index) => (
                                        <button
                                            key={`popular-${index}`}
                                            type="button"
                                            onClick={() => handleSuggestionClick(suggestion.text)}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center space-x-3"
                                        >
                                            <span className="text-lg">{suggestion.icon}</span>
                                            <span className="text-gray-700">{suggestion.text}</span>
                                            <svg className="w-4 h-4 text-gray-400 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7l10 10M7 17L17 7" />
                                            </svg>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Other suggestions */}
                            {otherSuggestions.length > 0 && (
                                <div>
                                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50">
                                        Suggestions
                                    </div>
                                    {otherSuggestions.map((suggestion, index) => (
                                        <button
                                            key={`other-${index}`}
                                            type="button"
                                            onClick={() => handleSuggestionClick(suggestion.text)}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center space-x-3"
                                        >
                                            <span className="text-lg">{suggestion.icon}</span>
                                            <span className="text-gray-700">{suggestion.text}</span>
                                            <svg className="w-4 h-4 text-gray-400 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7l10 10M7 17L17 7" />
                                            </svg>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* No suggestions */}
                            {filteredSuggestions.length === 0 && localValue && (
                                <div className="px-4 py-6 text-center text-gray-500">
                                    <svg className="w-8 h-8 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    <p className="text-sm">No suggestions found</p>
                                    <p className="text-xs text-gray-400 mt-1">Press Enter to search anyway</p>
                                </div>
                            )}
                        </div>
                    )}
                </form>

                {/* Search Tips */}
                <div className="mt-4 p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100">
                    <h4 className="text-sm font-semibold text-gray-800 mb-2">💡 Search Tips:</h4>
                    <ul className="text-xs text-gray-600 space-y-1">
                        <li>• Try keywords like "grammar", "vocabulary", "tips"</li>
                        <li>• Search for specific topics like "present tense"</li>
                        <li>• Use phrases like "how to improve speaking"</li>
                    </ul>
                </div>
            </div>
        </div>
    );
} 